import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {useForm} from "react-hook-form";
import {FormLabel, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import {useCreatePool, useQueryPool, useUpdatePool} from "@/service/pool.s";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {emptyValue} from "@/helper/method";
import {isNumber} from "lodash";

const validationSchema = z.object({
    id: z.number().nullable().default(0),
    poolName: z.string({message: "请填写客户名称"}).nonempty({message: "请填写客户名称"}),
    contact: z.string({message: "请填写联系人"}).nonempty({message: "请填写联系人"}),
    mobile: z.string({message: "请填写联系电话"}).nonempty({message: "请填写联系电话"}),
    location: z.unknown(),
    remark: z.unknown()
});

export type WritePoolForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export const WritePool = () => {
    const {write, id: drawerId} = useDrawerModal()
    const {setToast} = useGlobalActions()
    const {data: poolData} = useQueryPool({id: drawerId!}, isNumber(drawerId))
    const {mutateAsync: createPoolMutate, isPending: createPoolPending} = useCreatePool()
    const {mutateAsync: updatePoolMutate, isPending: updatePoolPending} = useUpdatePool()
    const pool = poolData?.data
    const {onSearch} = useDrawerModal()
    const {setDrawerModal} = useGlobalActions()

    const {
        register,
        reset,
        handleSubmit,
        formState: {errors},
    } = useForm<WritePoolForm>({
        resolver: zodResolver(validationSchema),
        values: pool as WritePoolForm,
        defaultValues: defaultValues,
    });

    const onSubmit = async (values: WritePoolForm) => {
        const result = write == 'add' ? await createPoolMutate(values) : await updatePoolMutate({...values, id: drawerId!})
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
            setToast({open: true, message: result.msg, severity: "success", variant: 'standard'})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }
    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>客户名称</FormLabel>
                        <TextField
                            error={!!errors?.poolName}
                            helperText={errors?.poolName?.message}
                            {...register("poolName")}
                            placeholder="填写客户名称"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>联系人</FormLabel>
                        <TextField
                            error={!!errors?.contact}
                            helperText={errors?.contact?.message}
                            {...register("contact")}
                            placeholder="填写联系人"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>联系电话</FormLabel>
                        <TextField
                            error={!!errors?.mobile}
                            helperText={errors?.mobile?.message}
                            {...register("mobile")}
                            placeholder="填写联系电话"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel>所在地区</FormLabel>
                        <TextField
                            {...register("location")}
                            placeholder="填写所在地区"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <FormLabel>备注</FormLabel>
                        <TextField
                            multiline
                            rows={3}
                            {...register("remark")}
                            slotProps={{
                                input: {
                                    sx: {height: 80},
                                    inputProps: {maxLength: 250},
                                },
                            }}
                            placeholder="填写备注"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createPoolPending || updatePoolPending} onReset={() => {
                reset(defaultValues)
            }}/>
        </Box>
    );
};
